import React, { useMemo, useRef, useState } from 'react'
import Swal from 'sweetalert2'

import { AppSelect } from '../../../../../../../components/Select'
import { KTIcon } from '../../../../../../../_metronic/helpers'
import { changeDisputeReason } from '../../core/api'
import { buildDisputeErrorText, disputeErrorFromException } from '../../core/errors'
import { buildReasonOptions, ReasonOption } from '../../core/reasonOptions'
import { DisputeRecord, DisputeReasonLookupItem } from '../../core/response.types'
import styles from './DisputeReasonCell.module.css'

interface Props {
  dispute: DisputeRecord
  reasons: DisputeReasonLookupItem[]
  onUpdated: (dispute: DisputeRecord) => void
}

const FALLBACK_ERROR = 'Не удалось изменить причину'

// the text may come from the backend: titleText renders it as text, title as HTML
const showError = (text: string) =>
  Swal.fire({ toast: true, position: 'top', icon: 'error', titleText: text, showConfirmButton: false, timer: 4000 })

export const DisputeReasonCell: React.FC<Props> = ({ dispute, reasons, onUpdated }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<number>(dispute.reason.id)
  const [saving, setSaving] = useState(false)
  // Ref, а не state: два нажатия подряд приходят до перерисовки, и замыкание видело бы старое значение.
  const menuOpen = useRef(false)

  const options = useMemo(() => buildReasonOptions(reasons, dispute.reason), [reasons, dispute.reason])

  if (!dispute.canEditReason) {
    return <>{dispute.reason.title}</>
  }

  const open = () => {
    setDraft(dispute.reason.id)
    setEditing(true)
  }

  const cancel = () => {
    if (!saving) setEditing(false)
  }

  const apply = async () => {
    if (saving) return
    if (draft === dispute.reason.id) {
      setEditing(false)
      return
    }

    setSaving(true)
    try {
      const r = await changeDisputeReason(dispute.id, { reasonId: draft })
      if (!r.data.success || !r.data.data) {
        showError(buildDisputeErrorText(r.data, FALLBACK_ERROR))
        return
      }
      onUpdated(r.data.data)
      setEditing(false)
    } catch (e: unknown) {
      showError(disputeErrorFromException(e, FALLBACK_ERROR))
    } finally {
      setSaving(false)
    }
  }

  // Пока меню открыто, Enter выбирает опцию, а Esc закрывает меню — это работа селекта.
  // При закрытом меню Enter применяет выбор, Esc отменяет правку. Ловим их в onKeyDown самого
  // селекта: Esc он гасит всегда, а Enter на кнопках ✓/✕ должен остаться их собственным кликом.
  const onSelectKeyDown = (e: React.KeyboardEvent) => {
    if (menuOpen.current) return
    if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    } else if (e.key === 'Enter') {
      e.preventDefault()
      void apply()
    }
  }

  if (!editing) {
    return (
      <button
        type='button'
        className={`btn btn-link p-0 text-start text-decoration-none fw-normal ${styles.reasonLink}`}
        title='Изменить причину'
        onClick={open}
      >
        {dispute.reason.title}
      </button>
    )
  }

  return (
    <div className='d-flex align-items-center gap-2'>
      <AppSelect<ReasonOption>
        aria-label='dispute-reason-select'
        autoFocus
        openMenuOnFocus
        // Фокус остаётся в поле после выбора — иначе Enter/Esc уже некому принять.
        blurInputOnSelect={false}
        onMenuOpen={() => (menuOpen.current = true)}
        onMenuClose={() => (menuOpen.current = false)}
        onKeyDown={onSelectKeyDown}
        isDisabled={saving}
        options={options}
        value={options.find((o) => o.value === draft) ?? null}
        onChange={(o) => o && setDraft(o.value)}
        styles={{ container: (base) => ({ ...base, minWidth: 220 }) }}
      />
      <button
        type='button'
        className='btn btn-sm btn-icon btn-light-success'
        title='Применить'
        onClick={apply}
        disabled={saving}
      >
        {saving ? (
          <span className='spinner-border spinner-border-sm' />
        ) : (
          <KTIcon iconName='check' className='fs-4' />
        )}
      </button>
      <button
        type='button'
        className='btn btn-sm btn-icon btn-light-danger'
        title='Отменить'
        onClick={cancel}
        disabled={saving}
      >
        <KTIcon iconName='cross' className='fs-4' />
      </button>
    </div>
  )
}
